import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../services/apiClient';

// Persisted across reloads/route changes so the conversation survives
// navigating around the app, but not shared between browser tabs/devices —
// matches backend/api/v1/chat.py's session model (no server-side hydration
// endpoint is called on mount, only used on the next /ask).
const SESSION_STORAGE_KEY = 'selltrix_chat_session_id';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: '¡Hola! Soy el asistente de soporte de Selltrix. Pregúntame lo que necesites sobre la plataforma.',
};

// Parses one SSE "data: {...}\n\n" frame out of the raw fetch stream.
// Plain fetch()+ReadableStream instead of EventSource because this is a
// POST request (EventSource only supports GET).
function parseSseEvents(buffer) {
  const events = [];
  let idx;
  while ((idx = buffer.indexOf('\n\n')) !== -1) {
    const raw = buffer.slice(0, idx).trim();
    buffer = buffer.slice(idx + 2);
    if (raw.startsWith('data:')) {
      try {
        events.push(JSON.parse(raw.slice(5).trim()));
      } catch {
        // Ignore a malformed frame rather than breaking the whole stream.
      }
    }
  }
  return { events, rest: buffer };
}

export default function SupportChatWidget() {
  const { user, authToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [escalateAvailable, setEscalateAvailable] = useState(false);
  const [escalating, setEscalating] = useState(false);
  // null | 'open' | 'in_analysis' — a pending manual support case for the
  // current session (see POST /api/chat/escalate + the admin support queue
  // in AdminPanel). Drives the "being reviewed" note and the status poll below.
  const [caseStatus, setCaseStatus] = useState(null);

  const sessionIdRef = useRef(
    typeof window !== 'undefined' ? localStorage.getItem(SESSION_STORAGE_KEY) : null
  );
  const listRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Cancel any in-flight stream if the widget unmounts (e.g. logout).
  useEffect(() => () => abortRef.current?.abort(), []);

  const authHeader = () => `Bearer ${authToken || localStorage.getItem('authToken')}`;

  // Checks whether an admin has answered the escalated case for this session.
  // Delivery is one-shot server-side (see GET /api/chat/case-status), so a
  // returned `answer` is appended exactly once even across repeated polls.
  const checkCaseStatus = async () => {
    if (!sessionIdRef.current) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/case-status/${sessionIdRef.current}`, {
        headers: { Authorization: authHeader() },
      });
      if (!res.ok) return;
      const data = await res.json();

      if (!data.has_case) {
        setCaseStatus(null);
        return;
      }
      if (data.status === 'answered' && data.answer) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.answer }]);
        toast.success('Un agente de soporte respondió tu caso');
        setCaseStatus(null);
        setEscalateAvailable(false);
      } else {
        setCaseStatus(data.status);
      }
    } catch (err) {
      console.error('[Chat] Error checking case status:', err);
    }
  };

  // One check whenever the widget is opened — picks up an answer that
  // arrived while it was closed, without needing a background poll.
  useEffect(() => {
    if (open) checkCaseStatus();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Not real-time: while the widget is open and a case is pending, poll for
  // the admin's answer every 25s instead. No polling while closed.
  useEffect(() => {
    if (!open || !caseStatus) return;
    const interval = setInterval(checkCaseStatus, 25000);
    return () => clearInterval(interval);
  }, [open, caseStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Consultants can't use Selltrix — the in-app support chat isn't for them.
  if (!user || user.role === 'consultant') return null;

  const appendToLastMessage = (token) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, content: last.content + token };
      return next;
    });
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    setEscalateAvailable(false);
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
        body: JSON.stringify({ message: text, session_id: sessionIdRef.current }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`El servidor respondió ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const { events, rest } = parseSseEvents(buffer);
        buffer = rest;

        for (const payload of events) {
          if (payload.type === 'token') {
            appendToLastMessage(payload.content);
          } else if (payload.type === 'done') {
            sessionIdRef.current = payload.session_id;
            localStorage.setItem(SESSION_STORAGE_KEY, payload.session_id);
            setEscalateAvailable(!!payload.escalate);
          } else if (payload.type === 'error') {
            throw new Error(payload.message || 'Error del asistente');
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[Chat] Error:', err);
      toast.error('No se pudo obtener respuesta del asistente. Intenta de nuevo.');
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Lo siento, tuve un problema para responder. Intenta de nuevo.',
        };
        return next;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleEscalate = async () => {
    if (!sessionIdRef.current) return;
    setEscalating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
        body: JSON.stringify({ session_id: sessionIdRef.current }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success('Le avisamos a nuestro equipo de soporte. Te contactaremos pronto.');
      setEscalateAvailable(false);
      setCaseStatus(data.status || 'open');
    } catch {
      toast.error('No se pudo notificar a soporte. Intenta de nuevo.');
    } finally {
      setEscalating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-24 right-5 z-[9998] w-[360px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[calc(100vh-8rem)] bg-popover border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="support-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card/60 shrink-0">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-semibold text-foreground">Soporte Selltrix</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Cerrar chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-muted text-foreground'
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                </div>
              </div>
            ))}

            {escalateAvailable && !streaming && (
              <div className="flex justify-start">
                <button
                  onClick={handleEscalate}
                  disabled={escalating}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition-colors disabled:opacity-60"
                >
                  {escalating ? <Loader2 className="w-3 h-3 animate-spin" /> : <LifeBuoy className="w-3 h-3" />}
                  ¿No resolvimos tu duda? Contactar a soporte
                </button>
              </div>
            )}

            {caseStatus && (
              <div className="flex justify-start">
                <div
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground"
                  data-testid="support-case-pending"
                >
                  <LifeBuoy className="w-3 h-3" />
                  {caseStatus === 'in_analysis'
                    ? 'Un agente está revisando tu caso…'
                    : 'Tu caso está en la fila de soporte…'}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end gap-2 p-3 border-t border-border shrink-0">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu pregunta…"
              rows={1}
              disabled={streaming}
              data-testid="support-chat-input"
              className="flex-1 resize-none max-h-24 px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
            />
            <button
              onClick={sendMessage}
              disabled={streaming || !input.trim()}
              data-testid="support-chat-send"
              aria-label="Enviar"
              className="p-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-muted disabled:cursor-not-allowed rounded-lg text-white transition-colors shrink-0"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="support-chat-fab"
        aria-label={open ? 'Cerrar chat de soporte' : 'Abrir chat de soporte'}
        className="fixed bottom-5 right-5 z-[9998] w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg flex items-center justify-center transition-colors"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>
    </>
  );
}
